"use client";

import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Hash, X, FileText, StickyNote, TrendingUp, CircleDot } from "lucide-react";
import { useStore } from "@/lib/store";

/**
 * Modal triggered when a user clicks a `#tag` link anywhere in rendered
 * Markdown. Shows every entity in the active workspace that is tagged
 * with this tag: docs, notes, issues (via labels), progress cards.
 */
export function TagView() {
  const activeTag = useStore((s) => s.activeTag);
  const setActiveTag = useStore((s) => s.setActiveTag);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const docs = useStore((s) => s.docs);
  const notes = useStore((s) => s.notes);
  const issues = useStore((s) => s.issues);
  const labels = useStore((s) => s.labels);
  const cards = useStore((s) => s.progressCards);
  const selectDoc = useStore((s) => s.selectDoc);
  const selectIssue = useStore((s) => s.selectIssue);

  const matches = useMemo(() => {
    if (!activeTag) return null;
    const t = activeTag.toLowerCase();
    const wsDocs = docs.filter(
      (d) =>
        d.workspaceId === activeWorkspaceId &&
        d.tags.some((x) => x.toLowerCase() === t)
    );
    const wsNotes = notes.filter(
      (n) =>
        n.workspaceId === activeWorkspaceId &&
        n.tags.some((x) => x.toLowerCase() === t)
    );
    // Issues don't have tags, they have labels. Treat labels whose name
    // matches the tag as the link.
    const matchingLabelIds = new Set(
      labels.filter((l) => l.name.toLowerCase() === t).map((l) => l.id)
    );
    const wsIssues = issues.filter(
      (i) =>
        i.workspaceId === activeWorkspaceId &&
        i.labelIds.some((lid) => matchingLabelIds.has(lid))
    );
    const wsCards = cards.filter((c) => c.workspaceId === activeWorkspaceId);
    // Progress cards don't have tags yet; include none for now.
    return { docs: wsDocs, notes: wsNotes, issues: wsIssues, cards: wsCards.filter(() => false) };
  }, [activeTag, docs, notes, issues, labels, cards, activeWorkspaceId]);

  return (
    <AnimatePresence>
      {activeTag && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.14 }}
            onClick={() => setActiveTag(null)}
            className="fixed inset-0 z-40 bg-black/40"
          />
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="fixed z-50 left-1/2 -translate-x-1/2 top-[10vh] w-full max-w-2xl max-h-[75vh] rounded-xl border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 h-11 border-b border-[var(--border)] bg-[var(--bg-secondary)]">
              <Hash size={13} className="text-[var(--accent)]" />
              <span className="text-[13px] font-semibold">
                #{activeTag}
              </span>
              <span className="text-[11px] text-[var(--fg-muted)]">
                {matches
                  ? `${matches.docs.length + matches.notes.length + matches.issues.length} items`
                  : ""}
              </span>
              <button
                onClick={() => setActiveTag(null)}
                className="ml-auto w-7 h-7 rounded hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)]"
                title="Close (Esc)"
              >
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-thin p-2">
              {matches && matches.docs.length === 0 && matches.notes.length === 0 && matches.issues.length === 0 && (
                <div className="text-center py-10 text-[12.5px] text-[var(--fg-muted)]">
                  Nothing tagged <span className="font-mono">#{activeTag}</span> in this workspace yet.
                </div>
              )}
              {matches && matches.docs.length > 0 && (
                <TagSection label="Docs" icon={<FileText size={11} />}>
                  {matches.docs.map((d) => (
                    <TagRow
                      key={d.id}
                      onClick={() => {
                        selectDoc(d.id);
                        setActiveTag(null);
                      }}
                      icon={<span>{d.icon}</span>}
                      title={d.title}
                      meta={d.tags.filter((x) => x.toLowerCase() !== activeTag.toLowerCase()).join(" · ")}
                    />
                  ))}
                </TagSection>
              )}
              {matches && matches.notes.length > 0 && (
                <TagSection label="Notes" icon={<StickyNote size={11} />}>
                  {matches.notes.map((n) => (
                    <TagRow
                      key={n.id}
                      onClick={() => {
                        setActiveTag(null);
                        // Notes aren't selectable via router yet; just close.
                      }}
                      icon={<StickyNote size={12} className="text-[var(--fg-tertiary)]" />}
                      title={n.body.slice(0, 100)}
                      meta={n.tags.filter((x) => x.toLowerCase() !== activeTag.toLowerCase()).join(" · ")}
                    />
                  ))}
                </TagSection>
              )}
              {matches && matches.issues.length > 0 && (
                <TagSection label="Tasks" icon={<CircleDot size={11} />}>
                  {matches.issues.map((i) => (
                    <TagRow
                      key={i.id}
                      onClick={() => {
                        selectIssue(i.id);
                        setActiveTag(null);
                      }}
                      icon={
                        <span className="font-mono text-[10px] text-[var(--fg-muted)]">
                          {i.identifier}
                        </span>
                      }
                      title={i.title}
                      meta={i.status}
                    />
                  ))}
                </TagSection>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function TagSection({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <div className="px-2 py-1.5 text-[10.5px] uppercase tracking-wide text-[var(--fg-muted)] font-medium flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function TagRow({
  icon,
  title,
  meta,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  meta?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--bg-hover)] text-left"
    >
      <span className="shrink-0 w-4 text-center">{icon}</span>
      <span className="flex-1 min-w-0 truncate text-[12.5px] text-[var(--fg-secondary)]">{title}</span>
      {meta && <span className="text-[10.5px] text-[var(--fg-muted)] truncate">{meta}</span>}
    </button>
  );
}

// Trending / Progress icon import symbol placeholder to keep TS happy for JSX usage
export { TrendingUp };
